(() => {
  'use strict';

  const services = Object.freeze({
    measuring: 'Measuring and fit',
    design: 'Choosing a style or fabric',
    privacy: 'Light control and privacy',
    controls: 'Controls or motorization',
    pricing: 'Configured price or checkout',
    other: 'Another question',
    'diy-installation': 'DIY installation and hardware',
    installation: 'Greater Boston design and installation',
    showroom: 'Planning a showroom visit',
    'full-service': 'Greater Boston full-service quote'
  });
  const notes = Object.freeze({
    measuring: 'Include your product and original readings.',
    design: 'Tell us about your room, light and fabric preferences.',
    privacy: 'Describe the daylight and nighttime privacy you want.',
    controls: 'Include your model and the controls or smart-home system you are considering.',
    pricing: 'Include the product, selected size and the price or checkout question.',
    other: 'Tell us what you would like our team to check.',
    'diy-installation': 'Include the model, mount and your installation or hardware question.',
    installation: 'Include your city, ZIP code and number of windows.',
    showroom: 'Tell us when you would like to visit Natick. Visits are confirmed by email.',
    'full-service': 'Include your city, ZIP code and number of windows. Full service is quoted separately.'
  });
  const preparation = Object.freeze({
    design: {
      items: ['Room and number of windows; product or fabric/color code if known.', 'Preferred style, wall colors and the light or privacy you want.', 'Any reference look you would like to discuss.'],
      placeholder: 'My room is… I am considering this product or fabric/color… The look and light I want are… My question is…'
    },
    privacy: {
      items: ['Room and your daylight and nighttime privacy goals.', 'Product, fabric/color and lining you are considering.', 'Any edge gaps, glare or additional privacy layer you want to discuss.'],
      placeholder: 'My room needs this daylight and nighttime privacy… The product/fabric/lining I am considering is… My question is…'
    },
    measuring: {
      items: ['Exact product, inside/outside mount and measurement unit.', 'Your original width and height readings, recess depth and any handles or locks.', 'For drapery: rod/track position and the finished size of each panel.'],
      placeholder: 'My product and mount are… My original readings and unit are… My recess depth or rod/track position is… The measurement I need help with is…'
    },
    'diy-installation': {
      items: ['Exact model, mount, control and cassette/valance selection.', 'Clear recess depth, handles/locks and the mounting surface.', 'The part or installation step you need help with; power or charging access if motorized.'],
      placeholder: 'My model, mount and controls are… My clear recess depth and mounting surface are… The installation part or step I need help with is…'
    },
    controls: {
      items: ['Product, selected motor/control and mount.', 'Remote, app or smart-home system you want to use.', 'Power/charging access and any accessories you already have.'],
      placeholder: 'My product and motor/control are… The remote/app/smart-home system I want to use is… My power access and controls question are…'
    },
    pricing: {
      items: ['Product, selected dimensions and options, and quantity.', 'Current configured total and any price or checkout message.', 'The amount or checkout step you want us to explain.'],
      placeholder: 'My product, size/options and quantity are… The configured price or checkout message says… My price question is…'
    },
    other: {
      items: ['Product or reference look, if you have one.', 'What you are trying to do and the step where you need help.', 'Relevant measurements, options or the message you see.'],
      placeholder: 'The product or project is… I need help at this step… My question and any relevant details are…'
    },
    local: {
      items: ['City, state and ZIP code.', 'Number of windows and the products or service you are considering.', 'Your question about service availability; local service is quoted separately.'],
      placeholder: 'My city/state/ZIP and number of windows are… The local service I am considering is… My question is…'
    },
    showroom: {
      items: ['Preferred dates for a Natick showroom visit.', 'Products or fabric/color codes you would like to compare.', 'Room notes and approximate window measurements to bring.'],
      placeholder: 'I would like to visit Natick on… The products or samples I want to see are… My showroom question is…'
    }
  });
  const localServices = new Set(['Greater Boston in-home measuring', 'Greater Boston design and installation', 'Greater Boston full-service quote']);
  const localRoutes = Object.freeze({ installation: 'in-home', showroom: 'showroom', 'full-service': 'in-home' });
  const referenceLooks = Object.freeze({
    'roller-01': Object.freeze({ label: 'Roller · Bathroom', photo: 'https://kaliashades.com/cdn/shop/files/kalia-real-roller-01.jpg?v=1792648001' }),
    'cellular-01': Object.freeze({ label: 'Cellular · Dining room', photo: 'https://kaliashades.com/cdn/shop/files/kalia-real-cellular-01.jpg?v=1792648001' }),
    'roller-02': Object.freeze({ label: 'Roller · Light filtering', photo: 'https://kaliashades.com/cdn/shop/files/kalia-real-roller-02.jpg?v=1792648002' }),
    'cellular-02': Object.freeze({ label: 'Cellular · Living room', photo: 'https://kaliashades.com/cdn/shop/files/kalia-real-cellular-02.jpg?v=1792648002' })
  });
  const referenceValue = look => `${look.label} — ${look.photo}`;
  const validProduct = value => value.length <= 160 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
  const oneParam = (params, key) => {
    const values = params.getAll(key);
    return values.length === 1 ? values[0] : '';
  };
  const storeRoot = root => {
    const route = root.querySelector('[data-full-service-route]');
    if (!route) return '/';
    try {
      const url = new URL(route.href, window.location.origin);
      const path = url.pathname.match(/^(\/(?:[a-z]{2,3})(?:-[a-z0-9]{2,8})*)?\/pages\/full-service$/i);
      if (url.origin === window.location.origin && !url.username && !url.password && path) return `${path[1] || ''}/`;
    } catch (_) { /* An invalid page anchor cannot supply product context. */ }
    return '/';
  };

  function initialize(root) {
    if (root.dataset.helpRequestReady === 'true') return;
    const select = root.querySelector('[data-help-service]');
    if (!select) return;
    root.dataset.helpRequestReady = 'true';
    const note = root.querySelector('[data-help-note]');
    const params = new URLSearchParams(window.location.search);
    const requested = oneParam(params, 'help');
    let product = oneParam(params, 'product');
    const restoredState = select.dataset.helpRestored === 'true' || Boolean(root.querySelector('.kg-error, [data-help-error]'));
    if (!restoredState && own(localRoutes, requested)) {
      const route = root.querySelector('[data-full-service-route]');
      if (route) {
        try {
          const destination = new URL(route.href, window.location.origin);
          if (destination.origin === window.location.origin && destination.pathname.endsWith('/pages/full-service') && !destination.username && !destination.password) {
            destination.search = '';
            destination.searchParams.set('service', localRoutes[requested]);
            destination.hash = 'service-request';
            window.location.replace(destination.href);
            return;
          }
        } catch (_) { /* An unavailable or invalid local-service route leaves the current form usable. */ }
      }
    }
    const location = root.querySelector('[data-help-location]');
    const question = root.querySelector('[data-help-question]');
    const prepareItems = [0, 1, 2].map(index => root.querySelector(`[data-help-prepare="${index}"]`));
    const updateState = () => {
      const service = Object.keys(services).find(key => services[key] === select.value);
      const profile = preparation[service] || (localServices.has(select.value) ? preparation.local : preparation.other);
      if (note) note.textContent = service ? notes[service] : localServices.has(select.value) ? notes.installation : 'Share what you need help with.';
      // Update guidance only: typed questions and guided configuration summaries stay intact.
      if (question) question.placeholder = profile.placeholder;
      prepareItems.forEach((item, index) => { if (item) item.textContent = profile.items[index]; });
      if (location) location.hidden = !localServices.has(select.value);
    };
    const choose = key => {
      if (!own(services, key)) return false;
      const option = Array.from(select.options).find(item => item.value === services[key]);
      if (!option) return false;
      select.value = option.value;
      updateState();
      return true;
    };

    if (!restoredState) choose(requested);
    updateState();
    select.addEventListener('change', updateState);
    root.querySelectorAll('[data-help-choice]').forEach(link => {
      link.addEventListener('click', event => {
        if (!choose(link.dataset.helpChoice)) return;
        event.preventDefault();
        select.focus();
      });
    });

    const lookField = root.querySelector('[data-help-look-value]');
    const lookContext = root.querySelector('[data-help-look]');
    const lookLabel = root.querySelector('[data-help-look-label]');
    const lookLink = root.querySelector('[data-help-look-link]');
    const requestedLook = oneParam(params, 'look');
    const look = restoredState && lookField?.value
      ? Object.values(referenceLooks).find(item => referenceValue(item) === lookField.value)
      : own(referenceLooks, requestedLook) ? referenceLooks[requestedLook] : null;
    if (look && lookField && lookContext && lookLabel && lookLink) {
      lookField.value = referenceValue(look);
      lookField.disabled = false;
      lookLabel.textContent = `Reference: ${look.label}`;
      lookLink.href = look.photo;
      lookContext.hidden = false;
    }

    const field = root.querySelector('[data-help-product-url]');
    const productPath = `${storeRoot(root)}products/`;
    if (restoredState && field?.value) {
      product = '';
      try {
        const restored = new URL(field.value);
        if (restored.origin === window.location.origin && !restored.username && !restored.password && !restored.search && !restored.hash) {
          const path = restored.pathname.startsWith(productPath) ? productPath : '/products/';
          if (restored.pathname.startsWith(path)) product = restored.pathname.slice(path.length);
        }
      } catch (_) { /* Invalid restored context stays disabled. */ }
    }
    if (!validProduct(product)) return;
    const context = root.querySelector('[data-help-product]');
    const link = root.querySelector('[data-help-product-link]');
    if (!field || !context || !link) return;
    const url = new URL(`${productPath}${product}`, window.location.origin);
    field.value = url.href;
    field.disabled = false;
    link.href = url.href;
    context.hidden = false;
    const interest = root.querySelector('[data-help-interest]');
    if (interest) interest.hidden = true;
  }

  function initializeAll() {
    document.querySelectorAll('[data-help-request]').forEach(initialize);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();
